import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  User, Package, MapPin, Zap, Truck, MessageSquare, StickyNote,
  Phone, Mail, RefreshCw, Repeat,
} from 'lucide-react';
import i18n from '../i18n';
import { usePageBreadcrumbStore } from '../store/pageBreadcrumbStore';
import type { ProduttoreInline } from '../types/produttore';
import { getRichiestaById, updateRichiesta, aggiornaNotaInterna, type AvvisoRiassegnazione, type CustomerUpdatePayload, type RichiestaDetail, type RichiestaUpdatePayload } from '../api/richieste';
import { getCerAttivi, type CerAttivo } from '../api/settings';
import { useRichiesteCountStore } from '../store/richiesteStore';
import { useCategorie } from '../hooks/useCategorie';
import { categoriaLabel, categoriaVariant } from '../lib/categorie';
import Select from '../components/ui/Select';
import { useAuthStore } from '../store/authStore';
import { FieldRow } from '../components/ui/DetailCard';
import EditableCard from '../components/ui/EditableCard';
import MaterialeFotoSection from '../components/richieste/MaterialeFotoSection';
import Badge from '../components/ui/Badge';
import { Skeleton } from '../components/ui/Skeleton';
import CambiaProduttoreModal from '../components/CambiaProduttoreModal';
import CambiaClienteModal from '../components/CambiaClienteModal';
import BadgeClienteRiassegnato from '../components/BadgeClienteRiassegnato';
import BadgeRevocaConferma from '../components/BadgeRevocaConferma';
import AvvisiRiassegnazione from '../components/AvvisiRiassegnazione';
import ConfermaModificaFiscaleModal from '../components/ConfermaModificaFiscaleModal';
import ConfermaPropagazioneLedgerModal from '../components/ConfermaPropagazioneLedgerModal';
import CerBloccatoModal from '../components/CerBloccatoModal';
import AvvisoBozzeAperte from '../components/AvvisoBozzeAperte';
import {
  detailTesto,
  parseConfermaFiscale,
  type ConfermaModificaFiscaleDetail,
  type FatturaRiferimento,
} from '../lib/modificaFiscale';
import {
  parseCerBloccato,
  parseConfermaPropagazione,
  type CerBloccatoDetail,
  type ConfermaPropagazioneDetail,
} from '../lib/cerLedger';
import WorkflowCard from '../components/workflow/WorkflowCard';
import ConsultazioniStorico from '../components/workflow/ConsultazioniStorico';
import DocumentiCard from '../components/orders/DocumentiCard';
import OmologaOrdineCard from '../components/orders/OmologaOrdineCard';
import ConferimentiCard from '../components/orders/ConferimentiCard';
import FatturazioneOrdineCard from '../components/orders/FatturazioneOrdineCard';
import { fmtKg } from '../lib/saldo';
import { ANAGRAFICHE_E_ORDINI_VIEW_ROLES, canCommerciale } from '../lib/constants';

// ── Helpers ─────────────────────────────────────────────────────────────────


function formatDateIT(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('it-IT', {
    day: '2-digit', month: 'long', year: 'numeric',
  });
}

function boolLabel(v: boolean | null): string | null {
  if (v === null || v === undefined) return null;
  return v ? i18n.t('common.si') : i18n.t('common.no');
}

function modalitaLabel(m: string): string {
  const MAP: Record<string, string> = {
    porto: i18n.t('richieste.campi.modalita_porto'),
    ritiro: i18n.t('richieste.campi.modalita_ritiro'),
    nd: i18n.t('richieste.campi.modalita_nd'),
  };
  return MAP[m] ?? m;
}

function statoMaterialeLabel(s: string | null): string | null {
  if (!s) return null;
  const MAP: Record<string, string> = {
    integro: i18n.t('richieste.campi.stato_integro'),
    parzialmente_danneggiato: i18n.t('richieste.campi.stato_parz_danneggiato'),
    rotto: i18n.t('richieste.campi.stato_rotto'),
  };
  return MAP[s] ?? s;
}

function frequenzaLabel(f: string | null): string | null {
  if (!f) return null;
  const MAP: Record<string, string> = {
    una_tantum: i18n.t('richieste.campi.freq_una_tantum'),
    mensile: i18n.t('richieste.campi.freq_mensile'),
    trimestrale: i18n.t('richieste.campi.freq_trimestrale'),
    da_definire: i18n.t('richieste.campi.freq_da_definire'),
  };
  return MAP[f] ?? f;
}

// ── Card produttore inline ────────────────────────────────────────────────────

// `label` = chiave i18n (tradotta al render).
function tipoSoggettoProdBadge(tipo: string | null) {
  if (!tipo) return null;
  const MAP: Record<string, { label: string; variant: 'info' | 'neutral' | 'purple' }> = {
    azienda: { label: 'anagrafiche.tipo_soggetto.azienda', variant: 'info' },
    privato: { label: 'anagrafiche.tipo_soggetto.privato', variant: 'neutral' },
    ente_pubblico: { label: 'anagrafiche.tipo_soggetto.ente_pubblico', variant: 'purple' },
  };
  return MAP[tipo] ?? null;
}

function ProduttoreInlineDisplay({ produttore, canLink }: { produttore: ProduttoreInline; canLink: boolean }) {
  const { t } = useTranslation();
  const tipo = tipoSoggettoProdBadge(produttore.tipo_soggetto);
  const identity = produttore.piva ?? produttore.codice_fiscale ?? null;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2 flex-wrap">
        {canLink ? (
          <a
            href={`/produttori/${produttore.id}`}
            className="font-medium text-sage hover:underline text-sm"
          >
            {produttore.ragione_sociale}
          </a>
        ) : (
          <span className="font-medium text-ink-dark text-sm">{produttore.ragione_sociale}</span>
        )}
        {tipo && <Badge variant={tipo.variant}>{t(tipo.label)}</Badge>}
      </div>
      {identity && (
        <div className="text-xs text-ink-soft font-mono">
          {produttore.piva
            ? t('richieste.dettaglio.piva_inline', { piva: produttore.piva })
            : t('richieste.dettaglio.cf_inline', { cf: produttore.codice_fiscale })}
        </div>
      )}
      {/* Blocco 6: il flag omologa_ricevuta è stato droppato dal produttore.
          L'indicatore "omologa ricevuta" deriverà dalla presenza di un'omologa (i18n-ignore: commento)
          valida associata all'ordine, esposta via `r.omologa` — UI dedicata
          al flusso ordini sarà rifinita in STEP 4 (#107). */}
      {canLink && (
        <a
          href={`/produttori/${produttore.id}`}
          className="inline-flex items-center gap-1 text-xs text-sage hover:underline mt-0.5"
        >
          {t('richieste.dettaglio.vai_al_produttore')}
        </a>
      )}
    </div>
  );
}

// ── Form input helpers ────────────────────────────────────────────────────────

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-4 py-1.5 border-b border-stone-mid last:border-0 items-start">
      <dt className="text-xs text-ink-soft min-w-[140px] pt-2 flex-shrink-0">{label}</dt>
      <dd className="flex-1">{children}</dd>
    </div>
  );
}

const inputCls = 'w-full text-sm text-ink-dark border border-stone-border rounded px-2 py-1.5 focus:outline-none focus:border-sage focus:ring-1 focus:ring-sage/20 bg-white';
const selectCls = inputCls;

// ── Loading skeleton ─────────────────────────────────────────────────────────

function PageSkeleton() {
  return (
    <div className="p-8 max-w-7xl mx-auto animate-pulse">
      <Skeleton className="h-5 w-32 mb-6" />
      <div className="flex gap-6">
        <div className="flex-[3] space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white border border-stone-mid rounded-xl overflow-hidden">
              <div className="px-5 py-3.5 border-b border-stone-mid bg-stone-bg">
                <Skeleton className="h-4 w-24" />
              </div>
              <div className="px-5 py-4 space-y-3">
                {[1, 2, 3].map((j) => <Skeleton key={j} className="h-4 w-full" />)}
              </div>
            </div>
          ))}
        </div>
        <div className="flex-[2] space-y-4">
          <div className="bg-white border border-stone-mid rounded-xl p-5 space-y-3">
            {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-5 w-full" />)}
          </div>
        </div>
      </div>
    </div>
  );
}


// ── Tipi per lo state di editing ─────────────────────────────────────────────

type CardName = 'cliente' | 'materiale' | 'origine' | 'gse' | 'trasportatore' | 'note' | 'nota_interna';

type FormValues = Record<string, string | boolean | number | null | undefined>;

// ── Pagina principale ────────────────────────────────────────────────────────

// Fase "Ordine" (post-conferma) per backPath e routing — allineato a order_status_v2 (#95).
// La fase Ordine è solo `in_progress` nel nuovo flusso; gli stati derivati Ordine
// (concluso/fatturato/pagato) non esistono ancora — rimandati al Blocco Conferimento.
const ORDINE_STATUSES = new Set(['in_progress']);

// Stati che possono avere documenti associati (decide se mostrare la DocumentiCard).
const DOCUMENTI_STATUSES = new Set([
  'preventivo_omologa_generati',
  'inviato_cliente',
  'confermato',
  'in_consultazione',
  'in_progress',
  'annullato',
]);

export default function DettaglioRichiestaPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();

  // Detect context: if route starts with /ordini or status is an ordine-status
  const isOrdineRoute = location.pathname.startsWith('/ordini');

  const refetchCount = useRichiesteCountStore((s) => s.refetch);
  const setSubLabel = usePageBreadcrumbStore((s) => s.setSubLabel);
  const { categorie: categorieList } = useCategorie();

  const [richiesta, setRichiesta] = useState<RichiestaDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Edit state
  const [currentEditingCard, setCurrentEditingCard] = useState<CardName | null>(null);
  const [formValues, setFormValues] = useState<FormValues>({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Guardia modifica dati fiscali (409): detail del backend + payload da
  // rinviare con `conferma_modifica_fiscale` una volta confermato.
  const [confermaFiscale, setConfermaFiscale] = useState<ConfermaModificaFiscaleDetail | null>(null);
  const [payloadInSospeso, setPayloadInSospeso] = useState<RichiestaUpdatePayload | null>(null);
  const [avvisoBozze, setAvvisoBozze] = useState<FatturaRiferimento[]>([]);

  // #116 — cambio CER con kg già a saldo: 409 confermabile (si rinvia con
  // `conferma_propagazione_ledger`) e i due 409 senza uscita, di sola lettura.
  // `payloadInSospeso` è lo stesso della guardia fiscale: le due conferme non
  // sono mai aperte insieme e, se una PATCH tocca sia anagrafica sia CER, il
  // reinvio confermato riparte dal payload già inviato e accumula i due flag.
  const [confermaPropagazione, setConfermaPropagazione] = useState<ConfermaPropagazioneDetail | null>(null);
  const [cerBloccato, setCerBloccato] = useState<CerBloccatoDetail | null>(null);

  // Cambia produttore modal
  const [showCambiaProduttoreModal, setShowCambiaProduttoreModal] = useState(false);
  // R6-C — riassegnazione cliente: modale + avvisi post-successo.
  const [showCambiaClienteModal, setShowCambiaClienteModal] = useState(false);
  const [avvisiRiassegnazione, setAvvisiRiassegnazione] = useState<AvvisoRiassegnazione[]>([]);
  const [produttoreToast, setProduttoreToast] = useState<string | null>(null);

  // Workflow toast
  const [workflowToast, setWorkflowToast] = useState<string | null>(null);
  // Esito di un'operazione avvenuta su un'ALTRA pagina che e' finita qui: oggi
  // l'eliminazione di un conferimento, che riporta all'ordine. Valore INIZIALE
  // e non effect — lo stesso trattamento dei deep-link: il messaggio arriva con
  // la navigazione, una volta sola.
  //
  // IL MESSAGGIO VIVE IN STATO LOCALE, non si rilegge da `location` a ogni
  // render: e' quello che permette di azzerare `location.state` subito e
  // lasciare comunque la striscia a video per i suoi 3 secondi.
  const [toastNavigazione, setToastNavigazione] = useState<string | null>(
    () => (location.state as { toast?: string } | null)?.toast ?? null);
  const statoNavigazioneConsumato = useRef(false);

  // Incrementato ad ogni aggiornamento workflow per forzare il refetch di DocumentiCard
  const [docsRefreshKey, setDocsRefreshKey] = useState(0);

  // CER dropdown state (used when editing card Materiale)
  const [cerList, setCerList] = useState<CerAttivo[]>([]);
  const [cerLoading, setCerLoading] = useState(false);
  const [cerError, setCerError] = useState<string | null>(null);

  // CONSUMA lo state della navigazione alla prima lettura. Il browser conserva
  // `location.state` anche attraverso un reload: senza questo, ricaricare la
  // pagina faceva ricomparire il toast di un'eliminazione avvenuta chissa'
  // quando. Azzerarlo non toglie nulla da video — il messaggio sta gia' in
  // `toastNavigazione`.
  //
  // IL REF, e non il solo `toastNavigazione`, e' cio' che lo rende UNA VOLTA
  // SOLA: `navigate` e `location.pathname` sono in dipendenza perche' l'effect
  // li usa, e se l'identita' di `navigate` cambiasse l'effect ripartirebbe.
  // Nessun cleanup qui, apposta: un riavvio dell'effect non deve poter
  // disfare niente (vedi il timer sotto, che invece un cleanup ce l'ha).
  useEffect(() => {
    if (!toastNavigazione || statoNavigazioneConsumato.current) return;
    statoNavigazioneConsumato.current = true;
    navigate(location.pathname, { replace: true, state: null });
  }, [toastNavigazione, navigate, location.pathname]);

  // E si chiude dopo 3 secondi, come gli altri due toast della pagina: la
  // striscia e' `fixed top-4 right-4 z-50` e, restando, copre i bottoni sotto.
  // DIPENDE DAL SOLO `toastNavigazione`: con dentro `navigate` o `location`,
  // un cambio di identita' farebbe girare il cleanup e il timer non
  // scadrebbe mai. E' il motivo per cui i due effect sono separati.
  useEffect(() => {
    if (!toastNavigazione) return;
    const timer = setTimeout(() => setToastNavigazione(null), 3000);
    return () => clearTimeout(timer);
  }, [toastNavigazione]);

  // Gli altri due toast della stessa striscia (riga 722: i tre stati si
  // mostrano nello stesso box). Erano due `setTimeout` senza `clearTimeout`
  // dentro i gestori: dopo lo smontaggio scattavano lo stesso e chiamavano
  // setState su un componente che non c'e' piu', e vitest usciva con 1 anche a
  // test tutti verdi, in modo dipendente dal carico. Tre effetti e non uno
  // perche' sono tre stati distinti: unirli vorrebbe dire azzerarli insieme.
  useEffect(() => {
    if (!produttoreToast) return;
    const timer = setTimeout(() => setProduttoreToast(null), 3000);
    return () => clearTimeout(timer);
  }, [produttoreToast]);

  useEffect(() => {
    if (!workflowToast) return;
    const timer = setTimeout(() => setWorkflowToast(null), 3000);
    return () => clearTimeout(timer);
  }, [workflowToast]);

  useEffect(() => {
    if (!id) return;
    void (async () => {
      setLoading(true);
      try {
        const r = await getRichiestaById(id);
        setRichiesta(r);
        setSubLabel(r.codice_ordine ?? null);
      } catch (err) {
        if ((err as { response?: { status?: number } })?.response?.status === 404) setNotFound(true);
        else setError(t('richieste.dettaglio.errore_caricamento'));
      } finally {
        setLoading(false);
      }
    })();
    return () => setSubLabel(null);
  }, [id, setSubLabel, t]);

  if (loading) return <PageSkeleton />;

  const backPath = isOrdineRoute || (richiesta && ORDINE_STATUSES.has(richiesta.status)) ? '/ordini' : '/richieste';
  const backLabel = backPath === '/ordini' ? t('common.nav.ordini') : t('common.nav.richieste');

  if (notFound) {
    return (
      <div className="p-8 text-center">
        <p className="text-2xl font-bold text-ink-dark mb-2">404</p>
        <p className="text-ink-soft mb-4">{t('richieste.dettaglio.non_trovata')}</p>
        <Link to={backPath} className="text-sage underline text-sm">{t('richieste.dettaglio.torna_lista')}</Link>
      </div>
    );
  }

  if (error || !richiesta) {
    return (
      <div className="p-8 text-center">
        <p className="text-accent-danger font-medium mb-4">{error ?? t('richieste.dettaglio.errore_sconosciuto')}</p>
        <button type="button" onClick={() => navigate(backPath)} className="text-sage underline text-sm">
          {t('richieste.dettaglio.torna_lista')}
        </button>
      </div>
    );
  }

  const r = richiesta;
  const userRole = user?.role ?? '';
  // AMM e' VIVO qui: la pagina e' montata anche sotto ordini/:id
  // (ANAGRAFICHE_E_ORDINI_VIEW_ROLES, AMM incluso), non solo sotto richieste/:id.
  // Difeso da DettaglioRichiestaPage.canEdit.test.tsx.
  const canEdit = ['admin', 'AMM', 'COMMERCIALE', 'DIR', 'RT'].includes(userRole);
  // R6-C: riassegnare il cliente e' un atto commerciale, AMM escluso (#172).
  // Gate cosmetico: l'autorita' resta il require_role del backend.
  const canRiassegnare = canCommerciale(userRole);
  const isPV = r.categoria === 'pv_pro' || r.categoria === 'pv_dom';
  const isSerr = r.categoria === 'serramenti';
  const isCavi = r.categoria === 'cavi';
  const hasGSE = r.conto_energia === 'si';
  const hasTras = r.tras_noto === true;

  // ── Edit handlers ─────────────────────────────────────────────────────────

  function handleEditStart(cardName: CardName, initialValues: FormValues) {
    if (currentEditingCard !== null && currentEditingCard !== cardName) {
      if (!window.confirm(t('richieste.dettaglio.modifiche_non_salvate'))) return;
    }
    setCurrentEditingCard(cardName);
    setFormValues(initialValues);
    setSaveError(null);

    if (cardName === 'materiale' && richiesta) {
      setCerLoading(true);
      setCerError(null);
      getCerAttivi(richiesta.categoria)
        .then(setCerList)
        .catch(() => setCerError(t('richieste.dettaglio.errore_cer')))
        .finally(() => setCerLoading(false));
    }
  }

  function handleCancel() {
    setCurrentEditingCard(null);
    setFormValues({});
    setSaveError(null);
    setCerError(null);
  }

  function setField(key: string, value: string | boolean | number | null) {
    setFormValues((prev) => ({ ...prev, [key]: value }));
  }

  async function inviaPatch(payload: RichiestaUpdatePayload) {
    if (!id) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      const updated = await updateRichiesta(id, payload);
      setRichiesta(updated);
      setCurrentEditingCard(null);
      setFormValues({});
      setConfermaFiscale(null);
      setConfermaPropagazione(null);
      setPayloadInSospeso(null);
      setAvvisoBozze(updated.avviso_bozze_aperte ?? []);
      refetchCount();
    } catch (err: unknown) {
      // 409 strutturato = serve conferma esplicita: si apre la modale e si
      // tiene da parte il payload per rinviarlo con il flag di conferma.
      const conferma = parseConfermaFiscale(err);
      if (conferma) {
        setConfermaFiscale(conferma);
        setPayloadInSospeso(payload);
        return;
      }
      // #116 — cambio CER: prima il confermabile, poi i due senza uscita.
      const propagazione = parseConfermaPropagazione(err);
      if (propagazione) {
        setConfermaPropagazione(propagazione);
        setPayloadInSospeso(payload);
        return;
      }
      const bloccato = parseCerBloccato(err);
      if (bloccato) {
        // Nessun payload da tenere: non c'è rilancio possibile.
        setCerBloccato(bloccato);
        return;
      }
      setSaveError(detailTesto(err) ?? t('richieste.dettaglio.errore_salvataggio'));
    } finally {
      setIsSaving(false);
    }
  }

  async function handleSave(buildPayload: () => RichiestaUpdatePayload) {
    setAvvisoBozze([]);
    await inviaPatch(buildPayload());
  }

  // V75 — la nota interna ha un endpoint suo: `updateRichiesta` usa
  // `exclude_none` lato backend e non saprebbe esprimere «svuota».
  async function handleSaveNotaInterna() {
    if (!id) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      setRichiesta(await aggiornaNotaInterna(id, String(formValues['nota_interna'] ?? '')));
      setCurrentEditingCard(null);
      setFormValues({});
    } catch (err: unknown) {
      setSaveError(detailTesto(err) ?? t('richieste.dettaglio.errore_salvataggio'));
    } finally {
      setIsSaving(false);
    }
  }

  async function onConfermaFiscale() {
    if (!payloadInSospeso) return;
    await inviaPatch({
      ...payloadInSospeso,
      customer: { ...payloadInSospeso.customer, conferma_modifica_fiscale: true },
    });
  }

  function annullaConfermaFiscale() {
    setConfermaFiscale(null);
    setPayloadInSospeso(null);
  }

  async function onConfermaPropagazione() {
    if (!payloadInSospeso) return;
    await inviaPatch({ ...payloadInSospeso, conferma_propagazione_ledger: true });
  }

  function annullaPropagazione() {
    setConfermaPropagazione(null);
    setPayloadInSospeso(null);
  }

  // ── Cambia Produttore handlers ────────────────────────────────────────────

  function handleClienteRiassegnato(updated: RichiestaDetail) {
    setRichiesta(updated);
    setShowCambiaClienteModal(false);
    setAvvisiRiassegnazione(updated.avvisi_riassegnazione ?? []);
  }

  function handleProduttoreSaved(updated: RichiestaDetail) {
    setRichiesta(updated);
    setShowCambiaProduttoreModal(false);
    setProduttoreToast(t('richieste.dettaglio.produttore_aggiornato'));
  }

  // ── Card Cliente ──────────────────────────────────────────────────────────

  function clienteInitialValues(): FormValues {
    return {
      ragione_sociale: r.customer.ragione_sociale ?? '',
      piva: r.customer.piva ?? '',
      cf: r.customer.cf ?? '',
      indirizzo: r.customer.indirizzo ?? '',
      citta: r.customer.citta ?? '',
      provincia: r.customer.provincia ?? '',
      cap: r.customer.cap ?? '',
      referente: r.customer.referente ?? '',
      email: r.customer.email ?? '',
      telefono: r.customer.telefono ?? '',
      pec: r.customer.pec ?? '',
    };
  }

  /**
   * Il backend usa `exclude_unset`: chiave ASSENTE = campo invariato, chiave a
   * `null` = cancellazione esplicita. I campi annullabili vanno quindi inviati
   * SEMPRE — anche vuoti, come `null` — altrimenti svuotarli è inesprimibile
   * (era il bug: il vecchio filtro `v !== ''` scartava proprio il caso da
   * esprimere). `ragione_sociale` ed `email` sono NOT NULL a schema: si
   * inviano solo se valorizzati (un null darebbe 422). Stesso contratto della
   * schermata anagrafica cliente.
   */
  function clientePayload(): RichiestaUpdatePayload {
    const c: CustomerUpdatePayload = {};
    const annullabili = ['piva', 'cf', 'indirizzo', 'citta', 'provincia', 'cap', 'referente', 'telefono', 'pec'] as const;
    for (const f of annullabili) {
      const v = formValues[f];
      c[f] = (typeof v === 'string' && v.trim() !== '') ? v.trim() : null;
    }
    for (const f of ['ragione_sociale', 'email'] as const) {
      const v = formValues[f];
      if (typeof v === 'string' && v.trim() !== '') c[f] = v.trim();
    }
    return { customer: c };
  }

  // ── Card Materiale ────────────────────────────────────────────────────────

  function materialeInitialValues(): FormValues {
    return {
      cer_attivo_id: r.cer_attivo_id ?? '',
      cer_code: r.cer_code ?? '',
      conto_energia: r.conto_energia ?? '',
      peso_stimato_kg: r.peso_stimato_kg ?? '',
      frequenza_conferimento: r.frequenza_conferimento ?? '',
      stato_materiale: r.stato_materiale ?? '',
      modalita_conferimento: r.modalita_conferimento ?? '',
      pv_n_pannelli: r.pv_n_pannelli ?? '',
      pv_marca: r.pv_marca ?? '',
      pv_modello: r.pv_modello ?? '',
      pv_smontati: r.pv_smontati ?? false,
      pv_imballaggio: r.pv_imballaggio ?? '',
      pv_vetri_rotti: r.pv_vetri_rotti ?? false,
      serr_materiale: r.serr_materiale ?? '',
      serr_peso_kg: r.serr_peso_kg ?? '',
      serr_n_infissi: r.serr_n_infissi ?? '',
      cavi_peso_kg: r.cavi_peso_kg ?? '',
      cavi_tipologia: r.cavi_tipologia ?? '',
      cavi_pct_rame: r.cavi_pct_rame ?? '',
      processo_generazione: r.processo_generazione ?? '',
    };
  }

  function materialePayload(): RichiestaUpdatePayload {
    const p: RichiestaUpdatePayload = {};
    const sv = (k: string) => { const v = formValues[k]; if (typeof v === 'string' && v !== '') return v; return undefined; };
    const nv = (k: string) => { const v = formValues[k]; if (v !== '' && v != null) return Number(v); return undefined; };
    const bv = (k: string) => formValues[k] as boolean | undefined;
    // V48: la riga CER (id) ha precedenza; il fallback free-text invia cer_code.
    if (sv('cer_attivo_id')) p.cer_attivo_id = sv('cer_attivo_id') as string;
    else if (sv('cer_code')) p.cer_code = sv('cer_code') as string;
    if (sv('conto_energia')) p.conto_energia = sv('conto_energia') as RichiestaUpdatePayload['conto_energia'];
    const ps = nv('peso_stimato_kg'); if (ps != null) p.peso_stimato_kg = ps;
    if (sv('frequenza_conferimento')) p.frequenza_conferimento = sv('frequenza_conferimento') as RichiestaUpdatePayload['frequenza_conferimento'];
    if (sv('stato_materiale')) p.stato_materiale = sv('stato_materiale') as RichiestaUpdatePayload['stato_materiale'];
    if (sv('modalita_conferimento')) p.modalita_conferimento = sv('modalita_conferimento') as RichiestaUpdatePayload['modalita_conferimento'];
    if (isPV) {
      const n = nv('pv_n_pannelli'); if (n != null) p.pv_n_pannelli = n;
      if (sv('pv_marca')) p.pv_marca = sv('pv_marca') as string;
      if (sv('pv_modello')) p.pv_modello = sv('pv_modello') as string;
      p.pv_smontati = bv('pv_smontati');
      if (sv('pv_imballaggio')) p.pv_imballaggio = sv('pv_imballaggio') as RichiestaUpdatePayload['pv_imballaggio'];
      p.pv_vetri_rotti = bv('pv_vetri_rotti');
    }
    if (isSerr) {
      if (sv('serr_materiale')) p.serr_materiale = sv('serr_materiale') as RichiestaUpdatePayload['serr_materiale'];
      const n = nv('serr_peso_kg'); if (n != null) p.serr_peso_kg = n;
      const ni = nv('serr_n_infissi'); if (ni != null) p.serr_n_infissi = ni;
    }
    if (isCavi) {
      const n = nv('cavi_peso_kg'); if (n != null) p.cavi_peso_kg = n;
      if (sv('cavi_tipologia')) p.cavi_tipologia = sv('cavi_tipologia') as RichiestaUpdatePayload['cavi_tipologia'];
      const pct = nv('cavi_pct_rame'); if (pct != null) p.cavi_pct_rame = pct;
    }
    // R2-E: testo libero, inviato sempre (stringa vuota = svuota il campo).
    p.processo_generazione = typeof formValues['processo_generazione'] === 'string'
      ? (formValues['processo_generazione'] as string) : '';
    return p;
  }

  // ── Card Origine ──────────────────────────────────────────────────────────

  function origineInitialValues(): FormValues {
    return {
      produttore_coincide: r.produttore_coincide ?? true,
      origine_indirizzo: r.origine_indirizzo ?? '',
      origine_comune: r.origine_comune ?? '',
      origine_provincia: r.origine_provincia ?? '',
      origine_cap: r.origine_cap ?? '',
    };
  }

  function originePayload(): RichiestaUpdatePayload {
    const p: RichiestaUpdatePayload = {};
    p.produttore_coincide = formValues['produttore_coincide'] as boolean;
    const sv = (k: string) => { const v = formValues[k]; if (typeof v === 'string' && v !== '') return v; return undefined; };
    if (sv('origine_indirizzo')) p.origine_indirizzo = sv('origine_indirizzo') as string;
    if (sv('origine_comune')) p.origine_comune = sv('origine_comune') as string;
    if (sv('origine_provincia')) p.origine_provincia = sv('origine_provincia') as string;
    if (sv('origine_cap')) p.origine_cap = sv('origine_cap') as string;
    return p;
  }

  // ── Card GSE ──────────────────────────────────────────────────────────────

  function gseInitialValues(): FormValues {
    return {
      gse_codice_impianto: r.gse_codice_impianto ?? '',
      gse_censimp: r.gse_censimp ?? '',
      gse_conto_energia: r.gse_conto_energia ?? '',
      gse_potenza_kw: r.gse_potenza_kw ?? '',
      gse_data_esercizio: r.gse_data_esercizio ?? '',
      gse_causale: r.gse_causale ?? '',
      gse_sogg_responsabile: r.gse_sogg_responsabile ?? false,
      gse_intestatario: r.gse_intestatario ?? '',
      gse_piva_cf: r.gse_piva_cf ?? '',
    };
  }

  function gsePayload(): RichiestaUpdatePayload {
    const p: RichiestaUpdatePayload = {};
    const sv = (k: string) => { const v = formValues[k]; if (typeof v === 'string' && v !== '') return v; return undefined; };
    if (sv('gse_codice_impianto')) p.gse_codice_impianto = sv('gse_codice_impianto') as string;
    if (sv('gse_censimp')) p.gse_censimp = sv('gse_censimp') as string;
    if (sv('gse_conto_energia')) p.gse_conto_energia = sv('gse_conto_energia') as RichiestaUpdatePayload['gse_conto_energia'];
    const kw = formValues['gse_potenza_kw']; if (kw !== '' && kw != null) p.gse_potenza_kw = Number(kw);
    if (sv('gse_data_esercizio')) p.gse_data_esercizio = sv('gse_data_esercizio') as string;
    if (sv('gse_causale')) p.gse_causale = sv('gse_causale') as RichiestaUpdatePayload['gse_causale'];
    p.gse_sogg_responsabile = formValues['gse_sogg_responsabile'] as boolean;
    if (sv('gse_intestatario')) p.gse_intestatario = sv('gse_intestatario') as string;
    if (sv('gse_piva_cf')) p.gse_piva_cf = sv('gse_piva_cf') as string;
    return p;
  }

  // ── Card Trasportatore ────────────────────────────────────────────────────

  function trasportatoreInitialValues(): FormValues {
    return {
      tras_noto: r.tras_noto ?? false,
      tras_ragione_sociale: r.tras_ragione_sociale ?? '',
      tras_anga: r.tras_anga ?? '',
      tras_note_logistiche: r.tras_note_logistiche ?? '',
    };
  }

  function trasportatorePayload(): RichiestaUpdatePayload {
    const p: RichiestaUpdatePayload = {};
    p.tras_noto = formValues['tras_noto'] as boolean;
    const sv = (k: string) => { const v = formValues[k]; if (typeof v === 'string' && v !== '') return v; return undefined; };
    if (sv('tras_ragione_sociale')) p.tras_ragione_sociale = sv('tras_ragione_sociale') as string;
    if (sv('tras_anga')) p.tras_anga = sv('tras_anga') as string;
    if (sv('tras_note_logistiche')) p.tras_note_logistiche = sv('tras_note_logistiche') as string;
    return p;
  }

  // ── Card Note ─────────────────────────────────────────────────────────────

  function noteInitialValues(): FormValues {
    return { note_cliente: r.note_cliente ?? '' };
  }

  function notePayload(): RichiestaUpdatePayload {
    const v = formValues['note_cliente'];
    return { note_cliente: typeof v === 'string' ? v : '' };
  }

  // ── Render ────────────────────────────────────────────────────────────────

  const editProps = (cardName: CardName, initialValues: () => FormValues, buildPayload: () => RichiestaUpdatePayload) => ({
    canEdit,
    isEditing: currentEditingCard === cardName,
    onEditStart: () => handleEditStart(cardName, initialValues()),
    onSave: () => handleSave(buildPayload),
    onCancel: handleCancel,
    isSaving: currentEditingCard === cardName && isSaving,
    error: currentEditingCard === cardName ? saveError : null,
  });

  const isEditingCliente = currentEditingCard === 'cliente';
  const isEditingMateriale = currentEditingCard === 'materiale';
  const isEditingOrigine = currentEditingCard === 'origine';
  const isEditingGSE = currentEditingCard === 'gse';
  const isEditingTras = currentEditingCard === 'trasportatore';
  const isEditingNote = currentEditingCard === 'note';
  const isEditingNotaInterna = currentEditingCard === 'nota_interna';

  return (
    <div className="p-8">
      {/* Toast */}
      {(produttoreToast || workflowToast || toastNavigazione) && (
        <div className="fixed top-4 right-4 z-50 px-4 py-3 bg-accent-success text-white text-sm font-medium rounded-lg shadow-lg"
          data-testid="richiesta-toast">
          {workflowToast ?? produttoreToast ?? toastNavigazione}
        </div>
      )}

      {/* Modal cambia produttore */}
      {showCambiaProduttoreModal && id && (
        <CambiaProduttoreModal
          richiestaId={id}
          customer={r.customer}
          onClose={() => setShowCambiaProduttoreModal(false)}
          onSaved={handleProduttoreSaved}
        />
      )}

      {/* R6-C — riassegnazione cliente */}
      {showCambiaClienteModal && id && (
        <CambiaClienteModal
          richiestaId={id}
          clienteAttualeId={r.customer.id}
          onClose={() => setShowCambiaClienteModal(false)}
          onSaved={handleClienteRiassegnato}
        />
      )}

      {/* Conferma modifica dati fiscali su cliente già fatturato (409) */}
      {confermaFiscale && (
        <ConfermaModificaFiscaleModal
          detail={confermaFiscale}
          isSaving={isSaving}
          onConfirm={() => { void onConfermaFiscale(); }}
          onCancel={annullaConfermaFiscale}
        />
      )}

      {/* #116 — cambio CER con kg a saldo: conferma dello spostamento (409) */}
      {confermaPropagazione && (
        <ConfermaPropagazioneLedgerModal
          detail={confermaPropagazione}
          isSaving={isSaving}
          onConfirm={() => { void onConfermaPropagazione(); }}
          onCancel={annullaPropagazione}
        />
      )}

      {/* #116 — cambio CER rifiutato: sola lettura, nessuna conferma possibile */}
      {cerBloccato && (
        <CerBloccatoModal detail={cerBloccato} onClose={() => setCerBloccato(null)} />
      )}

      <div className="max-w-7xl mx-auto">
        {avvisoBozze.length > 0 && (
          <div className="mb-4">
            <AvvisoBozzeAperte bozze={avvisoBozze} onDismiss={() => setAvvisoBozze([])} />
          </div>
        )}
        {avvisiRiassegnazione.length > 0 && (
          <div className="mb-4">
            <AvvisiRiassegnazione avvisi={avvisiRiassegnazione} onDismiss={() => setAvvisiRiassegnazione([])} />
          </div>
        )}
        {/* V68 — badge PERMANENTE della revoca: unica traccia stabile una
            volta che la rigenerazione ha cancellato i documenti generati. */}
        {r.revoca && (
          <div className="mb-4">
            <BadgeRevocaConferma revoca={r.revoca} />
          </div>
        )}
        {/* Badge PERMANENTE: resta finche' l'ordine ha un cliente precedente. */}
        {r.cliente_precedente && (
          <div className="mb-4">
            <BadgeClienteRiassegnato precedente={r.cliente_precedente} />
          </div>
        )}
        <div className="flex gap-6 items-start">
          {/* ── Colonna sinistra (60%) ── */}
          <div className="flex-[3] min-w-0 space-y-4">

            {/* ── Card Cliente ── */}
            <EditableCard
              title={t('richieste.dettaglio.card_cliente')}
              icon={User}
              testid="card-cliente"
              {...editProps('cliente', clienteInitialValues, clientePayload)}
            >
              {isEditingCliente ? (
                <dl className="divide-y divide-stone-mid">
                  {/* Non modificabili */}
                  <FieldRow label={t('richieste.dettaglio.referente_snapshot')} value={r.referente_richiesta} />
                  <FormField label={t('anagrafiche.campi.ragione_sociale')}>
                    <input className={inputCls} value={String(formValues['ragione_sociale'] ?? '')} onChange={(e) => setField('ragione_sociale', e.target.value)} />
                  </FormField>
                  <FormField label={t('anagrafiche.campi.partita_iva')}>
                    <input className={inputCls} data-testid="input-customer-piva" value={String(formValues['piva'] ?? '')} onChange={(e) => setField('piva', e.target.value)} maxLength={11} pattern="\d{11}" />
                  </FormField>
                  <FormField label={t('anagrafiche.campi.codice_fiscale')}>
                    <input className={inputCls} value={String(formValues['cf'] ?? '')} onChange={(e) => setField('cf', e.target.value)} maxLength={16} />
                  </FormField>
                  <FormField label={t('anagrafiche.campi.sede_legale')}>
                    <input className={inputCls} value={String(formValues['indirizzo'] ?? '')} onChange={(e) => setField('indirizzo', e.target.value)} />
                  </FormField>
                  <FormField label={t('anagrafiche.campi.citta')}>
                    <input className={inputCls} value={String(formValues['citta'] ?? '')} onChange={(e) => setField('citta', e.target.value)} />
                  </FormField>
                  <FormField label={t('anagrafiche.campi.provincia')}>
                    <input className={inputCls} value={String(formValues['provincia'] ?? '')} onChange={(e) => setField('provincia', e.target.value)} maxLength={2} />
                  </FormField>
                  <FormField label={t('anagrafiche.campi.cap')}>
                    <input className={inputCls} value={String(formValues['cap'] ?? '')} onChange={(e) => setField('cap', e.target.value)} maxLength={5} />
                  </FormField>
                  <FormField label={t('anagrafiche.campi.referente')}>
                    <input className={inputCls} value={String(formValues['referente'] ?? '')} onChange={(e) => setField('referente', e.target.value)} />
                  </FormField>
                  <FormField label={t('anagrafiche.campi.email')}>
                    <input className={inputCls} type="email" value={String(formValues['email'] ?? '')} onChange={(e) => setField('email', e.target.value)} />
                  </FormField>
                  <FormField label={t('anagrafiche.campi.telefono')}>
                    <input className={inputCls} value={String(formValues['telefono'] ?? '')} onChange={(e) => setField('telefono', e.target.value)} />
                  </FormField>
                  <FormField label={t('anagrafiche.campi.pec')}>
                    <input className={inputCls} type="email" value={String(formValues['pec'] ?? '')} onChange={(e) => setField('pec', e.target.value)} />
                  </FormField>
                </dl>
              ) : (
                <dl className="divide-y divide-stone-mid">
                  <FieldRow label={t('anagrafiche.campi.ragione_sociale')} value={r.customer.ragione_sociale} />
                  <FieldRow label={t('anagrafiche.campi.partita_iva')} value={r.customer.piva} mono testid="valore-customer-piva" />
                  <FieldRow label={t('anagrafiche.campi.codice_fiscale')} value={r.customer.cf} mono />
                  <FieldRow label={t('anagrafiche.campi.sede_legale')} value={r.customer.indirizzo} />
                  {r.customer.citta && <FieldRow label={t('anagrafiche.campi.citta')} value={r.customer.citta} />}
                  {r.customer.provincia && <FieldRow label={t('anagrafiche.campi.provincia')} value={r.customer.provincia} />}
                  {r.customer.cap && <FieldRow label={t('anagrafiche.campi.cap')} value={r.customer.cap} />}
                  <FieldRow label={t('anagrafiche.campi.referente')} value={r.customer.referente} />
                  <FieldRow
                    label={t('anagrafiche.campi.telefono')}
                    value={
                      r.customer.telefono ? (
                        <a href={`tel:${r.customer.telefono}`} className="text-sage hover:underline flex items-center gap-1">
                          <Phone size={12} />{r.customer.telefono}
                        </a>
                      ) : null
                    }
                  />
                  <FieldRow
                    label={t('anagrafiche.campi.email')}
                    value={
                      <a href={`mailto:${r.customer.email}`} className="text-sage hover:underline flex items-center gap-1">
                        <Mail size={12} />{r.customer.email}
                      </a>
                    }
                  />
                  {r.customer.pec && <FieldRow label={t('anagrafiche.campi.pec')} value={r.customer.pec} />}
                </dl>
              )}
              {!isEditingCliente && (r.referente_richiesta !== r.customer.referente || r.telefono_richiesta !== r.customer.telefono) && (
                <div className="mt-3 pt-3 border-t border-stone-mid">
                  <p className="text-xs text-ink-faint mb-2 font-medium uppercase tracking-wide">{t('richieste.dettaglio.contatti_richiesta')}</p>
                  <dl>
                    <FieldRow label={t('anagrafiche.campi.referente')} value={r.referente_richiesta} />
                    <FieldRow label={t('anagrafiche.campi.telefono')} value={r.telefono_richiesta} />
                    <FieldRow label={t('anagrafiche.campi.email')} value={r.email_richiesta} />
                  </dl>
                </div>
              )}
              {!isEditingCliente && canRiassegnare && (
                <div className="mt-3 pt-3 border-t border-stone-mid">
                  <button
                    type="button"
                    data-testid="btn-cambia-cliente"
                    onClick={() => setShowCambiaClienteModal(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-ink-mid border border-stone-border rounded-md hover:bg-stone-bg transition-colors"
                  >
                    <Repeat size={12} />
                    {t('richieste.cambia_cliente.apri')}
                  </button>
                </div>
              )}
            </EditableCard>

            {/* ── Card Materiale ── */}
            <EditableCard
              title={t('richieste.dettaglio.card_materiale')}
              icon={Package}
              testid="card-materiale"
              {...editProps('materiale', materialeInitialValues, materialePayload)}
            >
              {isEditingMateriale ? (
                <dl className="divide-y divide-stone-mid">
                  {/* Categoria non modificabile */}
                  <FieldRow label={t('richieste.campi.categoria')} value={
                    <Badge variant={categoriaVariant(r.categoria)}>
                      {categoriaLabel(r.categoria, categorieList)}
                    </Badge>
                  } />
                  <FormField label={t('richieste.campi.codice_cer')}>
                    {cerLoading ? (
                      <div className={`${inputCls} text-ink-soft`}>{t('richieste.dettaglio.caricamento_cer')}</div>
                    ) : cerError ? (
                      <div>
                        <div className="text-xs text-accent-danger mb-1">{cerError}</div>
                        <input className={inputCls} value={String(formValues['cer_code'] ?? '')} onChange={(e) => setField('cer_code', e.target.value)} placeholder={t('richieste.dettaglio.inserisci_manualmente')} />
                      </div>
                    ) : (
                      // V48: si seleziona la RIGA CER (id). Il BE sincronizza cer_code.
                      <Select
                        value={String(formValues['cer_attivo_id'] ?? '')}
                        onChange={(v) => setField('cer_attivo_id', v)}
                        options={cerList.map((c) => ({
                          value: c.id ?? '',
                          label: `${c.cer} — ${c.nome_descrittivo}`,
                        }))}
                        placeholder={t('richieste.dettaglio.seleziona_cer')}
                      />
                    )}
                  </FormField>
                  <FormField label={t('richieste.campi.conto_energia')}>
                    <select className={selectCls} value={String(formValues['conto_energia'] ?? '')} onChange={(e) => setField('conto_energia', e.target.value)}>
                      <option value="">{t('common.seleziona')}</option>
                      <option value="si">{t('common.si')}</option>
                      <option value="no">{t('common.no')}</option>
                      <option value="non_so">{t('richieste.campi.conto_energia_non_so')}</option>
                    </select>
                  </FormField>
                  <FormField label={t('richieste.campi.stato_materiale')}>
                    <select className={selectCls} value={String(formValues['stato_materiale'] ?? '')} onChange={(e) => setField('stato_materiale', e.target.value)}>
                      <option value="">{t('common.seleziona')}</option>
                      <option value="integro">{t('richieste.campi.stato_integro')}</option>
                      <option value="parzialmente_danneggiato">{t('richieste.campi.stato_parz_danneggiato')}</option>
                      <option value="rotto">{t('richieste.campi.stato_rotto')}</option>
                    </select>
                  </FormField>
                  <FormField label={t('richieste.campi.frequenza')}>
                    <select className={selectCls} value={String(formValues['frequenza_conferimento'] ?? '')} onChange={(e) => setField('frequenza_conferimento', e.target.value)}>
                      <option value="">{t('common.seleziona')}</option>
                      <option value="una_tantum">{t('richieste.campi.freq_una_tantum')}</option>
                      <option value="mensile">{t('richieste.campi.freq_mensile')}</option>
                      <option value="trimestrale">{t('richieste.campi.freq_trimestrale')}</option>
                      <option value="da_definire">{t('richieste.campi.freq_da_definire')}</option>
                    </select>
                  </FormField>
                  <FormField label={t('richieste.campi.modalita_conferimento')}>
                    <select className={selectCls} value={String(formValues['modalita_conferimento'] ?? '')} onChange={(e) => setField('modalita_conferimento', e.target.value)}>
                      <option value="">{t('common.seleziona')}</option>
                      <option value="porto">{t('richieste.campi.modalita_porto')}</option>
                      <option value="ritiro">{t('richieste.campi.modalita_ritiro')}</option>
                      <option value="nd">{t('richieste.campi.modalita_nd')}</option>
                    </select>
                  </FormField>
                  <FormField label={t('richieste.campi.peso_stimato_ordine')}>
                    <input className={inputCls} type="number" min={0} step="0.01" value={String(formValues['peso_stimato_kg'] ?? '')} onChange={(e) => setField('peso_stimato_kg', e.target.value)} />
                  </FormField>
                  <FormField label={t('richieste.campi.processo_generazione')}>
                    <textarea className={inputCls} rows={2} data-testid="input-processo-generazione"
                      value={String(formValues['processo_generazione'] ?? '')}
                      onChange={(e) => setField('processo_generazione', e.target.value)} />
                  </FormField>
                  {isPV && (
                    <>
                      <FormField label={t('richieste.campi.n_pannelli')}>
                        <input className={inputCls} type="number" min={0} value={String(formValues['pv_n_pannelli'] ?? '')} onChange={(e) => setField('pv_n_pannelli', e.target.value)} />
                      </FormField>
                      <FormField label={t('richieste.campi.marca')}>
                        <input className={inputCls} value={String(formValues['pv_marca'] ?? '')} onChange={(e) => setField('pv_marca', e.target.value)} />
                      </FormField>
                      <FormField label={t('richieste.campi.modello')}>
                        <input className={inputCls} value={String(formValues['pv_modello'] ?? '')} onChange={(e) => setField('pv_modello', e.target.value)} />
                      </FormField>
                      <FormField label={t('richieste.campi.gia_smontati')}>
                        <select className={selectCls} value={String(formValues['pv_smontati'] ?? '')} onChange={(e) => setField('pv_smontati', e.target.value === 'true')}>
                          <option value="">{t('common.seleziona')}</option>
                          <option value="true">{t('common.si')}</option>
                          <option value="false">{t('common.no')}</option>
                        </select>
                      </FormField>
                      <FormField label={t('richieste.campi.imballaggio')}>
                        <select className={selectCls} value={String(formValues['pv_imballaggio'] ?? '')} onChange={(e) => setField('pv_imballaggio', e.target.value)}>
                          <option value="">{t('common.seleziona')}</option>
                          <option value="bancali">{t('richieste.campi.imb_bancali')}</option>
                          <option value="big_bags">{t('richieste.campi.imb_big_bags')}</option>
                          <option value="sfusi">{t('richieste.campi.imb_sfusi')}</option>
                          <option value="da_definire">{t('richieste.campi.freq_da_definire')}</option>
                        </select>
                      </FormField>
                      <FormField label={t('richieste.campi.vetri_rotti')}>
                        <select className={selectCls} value={String(formValues['pv_vetri_rotti'] ?? '')} onChange={(e) => setField('pv_vetri_rotti', e.target.value === 'true')}>
                          <option value="">{t('common.seleziona')}</option>
                          <option value="true">{t('common.si')}</option>
                          <option value="false">{t('common.no')}</option>
                        </select>
                      </FormField>
                    </>
                  )}
                  {isSerr && (
                    <>
                      <FormField label={t('richieste.campi.materiale')}>
                        <select className={selectCls} value={String(formValues['serr_materiale'] ?? '')} onChange={(e) => setField('serr_materiale', e.target.value)}>
                          <option value="">{t('common.seleziona')}</option>
                          <option value="pvc">PVC</option>
                          <option value="alluminio">{t('richieste.campi.mat_alluminio')}</option>
                          <option value="misto">{t('richieste.campi.mat_misto')}</option>
                        </select>
                      </FormField>
                      <FormField label={t('richieste.campi.peso_stimato')}>
                        <input className={inputCls} type="number" min={0} step="0.01" value={String(formValues['serr_peso_kg'] ?? '')} onChange={(e) => setField('serr_peso_kg', e.target.value)} />
                      </FormField>
                      <FormField label={t('richieste.campi.n_infissi')}>
                        <input className={inputCls} type="number" min={0} value={String(formValues['serr_n_infissi'] ?? '')} onChange={(e) => setField('serr_n_infissi', e.target.value)} />
                      </FormField>
                    </>
                  )}
                  {isCavi && (
                    <>
                      <FormField label={t('richieste.campi.peso_stimato')}>
                        <input className={inputCls} type="number" min={0} step="0.01" value={String(formValues['cavi_peso_kg'] ?? '')} onChange={(e) => setField('cavi_peso_kg', e.target.value)} />
                      </FormField>
                      <FormField label={t('richieste.campi.tipologia')}>
                        <select className={selectCls} value={String(formValues['cavi_tipologia'] ?? '')} onChange={(e) => setField('cavi_tipologia', e.target.value)}>
                          <option value="">{t('common.seleziona')}</option>
                          <option value="rame_puro">{t('richieste.campi.cavi_rame_puro')}</option>
                          <option value="rame_pvc">{t('richieste.campi.cavi_rame_pvc')}</option>
                          <option value="misto">{t('richieste.campi.mat_misto')}</option>
                        </select>
                      </FormField>
                      <FormField label={t('richieste.campi.pct_rame')}>
                        <input className={inputCls} type="number" min={0} max={100} step="0.1" value={String(formValues['cavi_pct_rame'] ?? '')} onChange={(e) => setField('cavi_pct_rame', e.target.value)} />
                      </FormField>
                    </>
                  )}
                </dl>
              ) : (
                <dl className="divide-y divide-stone-mid">
                  <FieldRow label={t('richieste.campi.categoria')} value={
                    <Badge variant={categoriaVariant(r.categoria)}>
                      {categoriaLabel(r.categoria, categorieList)}
                    </Badge>
                  } />
                  <FieldRow label={t('richieste.campi.conto_energia')} value={
                    r.conto_energia === 'si' ? (
                      <span className="flex items-center gap-1 text-accent-warning font-medium">
                        <Zap size={13} /> {t('richieste.dettaglio.conto_energia_attivo')}
                      </span>
                    ) : r.conto_energia === 'no' ? t('common.no') : r.conto_energia === 'non_so' ? t('richieste.campi.conto_energia_non_so') : null
                  } />
                  <FieldRow label={t('richieste.campi.stato_materiale')} value={statoMaterialeLabel(r.stato_materiale)} />
                  <FieldRow label={t('richieste.campi.frequenza')} value={frequenzaLabel(r.frequenza_conferimento)} />
                  <FieldRow label={t('richieste.campi.modalita_conferimento')} value={modalitaLabel(r.modalita_conferimento)} />
                  <FieldRow label={t('richieste.campi.peso_stimato_ordine')} value={r.peso_stimato_kg != null ? `${fmtKg(r.peso_stimato_kg)} kg` : null} />
                  <FieldRow label={t('richieste.campi.processo_generazione')} value={r.processo_generazione} />
                  {isPV && (
                    <>
                      <FieldRow label={t('richieste.campi.n_pannelli')} value={r.pv_n_pannelli} />
                      <FieldRow label={t('richieste.campi.marca')} value={r.pv_marca} />
                      <FieldRow label={t('richieste.campi.modello')} value={r.pv_modello} />
                      <FieldRow label={t('richieste.campi.gia_smontati')} value={boolLabel(r.pv_smontati)} />
                      <FieldRow label={t('richieste.campi.imballaggio')} value={r.pv_imballaggio} />
                      <FieldRow label={t('richieste.campi.vetri_rotti')} value={boolLabel(r.pv_vetri_rotti)} />
                    </>
                  )}
                  {isSerr && (
                    <>
                      <FieldRow label={t('richieste.campi.materiale')} value={r.serr_materiale} />
                      <FieldRow label={t('richieste.campi.peso_stimato')} value={r.serr_peso_kg != null ? fmtKg(r.serr_peso_kg) : null} />
                      <FieldRow label={t('richieste.campi.n_infissi')} value={r.serr_n_infissi} />
                    </>
                  )}
                  {isCavi && (
                    <>
                      <FieldRow label={t('richieste.campi.peso_stimato')} value={r.cavi_peso_kg != null ? fmtKg(r.cavi_peso_kg) : null} />
                      <FieldRow label={t('richieste.campi.tipologia')} value={r.cavi_tipologia} />
                      <FieldRow label={t('richieste.campi.pct_rame')} value={r.cavi_pct_rame != null ? `${r.cavi_pct_rame}%` : null} />
                    </>
                  )}
                </dl>
              )}
              {/* R5/F6 — foto del materiale: scrittura come l'edit del materiale (AMM escluso). */}
              {id && (
                <MaterialeFotoSection
                  ordineId={id}
                  canWrite={canCommerciale(userRole)}
                />
              )}
            </EditableCard>

            {/* ── Card Origine ── */}
            <EditableCard
              title={t('richieste.dettaglio.card_origine')}
              icon={MapPin}
              {...editProps('origine', origineInitialValues, originePayload)}
            >
              {isEditingOrigine ? (
                <dl className="divide-y divide-stone-mid">
                  <FormField label={t('richieste.campi.produttore_uguale_cliente')}>
                    <select className={selectCls} value={String(formValues['produttore_coincide'] ?? 'true')} onChange={(e) => setField('produttore_coincide', e.target.value === 'true')}>
                      <option value="true">{t('common.si')}</option>
                      <option value="false">{t('common.no')}</option>
                    </select>
                  </FormField>
                  <FormField label={t('anagrafiche.campi.indirizzo')}>
                    <input className={inputCls} value={String(formValues['origine_indirizzo'] ?? '')} onChange={(e) => setField('origine_indirizzo', e.target.value)} />
                  </FormField>
                  <FormField label={t('anagrafiche.campi.comune')}>
                    <input className={inputCls} value={String(formValues['origine_comune'] ?? '')} onChange={(e) => setField('origine_comune', e.target.value)} />
                  </FormField>
                  <FormField label={t('anagrafiche.campi.provincia')}>
                    <input className={inputCls} value={String(formValues['origine_provincia'] ?? '')} onChange={(e) => setField('origine_provincia', e.target.value)} maxLength={2} />
                  </FormField>
                  <FormField label={t('anagrafiche.campi.cap')}>
                    <input className={inputCls} value={String(formValues['origine_cap'] ?? '')} onChange={(e) => setField('origine_cap', e.target.value)} maxLength={5} inputMode="numeric" />
                  </FormField>
                </dl>
              ) : (
                <>
                  <dl className="divide-y divide-stone-mid">
                    <FieldRow label={t('anagrafiche.campi.indirizzo')} value={r.origine_indirizzo} />
                    <FieldRow label={t('anagrafiche.campi.comune')} value={r.origine_comune} />
                    <FieldRow label={t('anagrafiche.campi.provincia')} value={r.origine_provincia} />
                    <FieldRow label={t('anagrafiche.campi.cap')} value={r.origine_cap} />
                    <FieldRow label={t('richieste.campi.produttore_uguale_cliente')} value={boolLabel(r.produttore_coincide)} />
                    <FieldRow label={t('richieste.campi.produttore')} value={
                      r.produttore !== null ? (
                        <ProduttoreInlineDisplay
                          produttore={r.produttore}
                          canLink={(ANAGRAFICHE_E_ORDINI_VIEW_ROLES as string[]).includes(userRole)}
                        />
                      ) : r.produttore_coincide ? (
                        <span className="text-ink-soft text-xs italic">{t('richieste.dettaglio.produttore_coincide')}</span>
                      ) : (
                        <span className="text-ink-soft text-xs italic">{t('richieste.dettaglio.produttore_non_fornito')}</span>
                      )
                    } />
                  </dl>
                  {canEdit && (
                    <div className="mt-3 pt-3 border-t border-stone-mid">
                      <button
                        type="button"
                        onClick={() => setShowCambiaProduttoreModal(true)}
                        className="flex items-center gap-1.5 text-xs font-medium text-sage border border-sage rounded-md px-3 py-1.5 hover:bg-sage-light transition-colors"
                      >
                        <RefreshCw size={12} />
                        Cambia produttore
                      </button>
                    </div>
                  )}
                </>
              )}
            </EditableCard>

            {/* ── Card GSE ── */}
            {(hasGSE || isEditingGSE) && (
              <EditableCard
                title={t('richieste.dettaglio.card_gse')}
                icon={Zap}
                {...editProps('gse', gseInitialValues, gsePayload)}
              >
                {isEditingGSE ? (
                  <dl className="divide-y divide-stone-mid">
                    <FormField label={t('richieste.campi.gse_codice_impianto')}>
                      <input className={inputCls} value={String(formValues['gse_codice_impianto'] ?? '')} onChange={(e) => setField('gse_codice_impianto', e.target.value)} />
                    </FormField>
                    <FormField label={t('richieste.campi.gse_censimp')}>
                      <input className={inputCls} value={String(formValues['gse_censimp'] ?? '')} onChange={(e) => setField('gse_censimp', e.target.value)} />
                    </FormField>
                    <FormField label={t('richieste.campi.conto_energia')}>
                      <select className={selectCls} value={String(formValues['gse_conto_energia'] ?? '')} onChange={(e) => setField('gse_conto_energia', e.target.value)}>
                        <option value="">{t('common.seleziona')}</option>
                        {['I', 'II', 'III', 'IV', 'V'].map((v) => <option key={v} value={v}>{t('richieste.campi.conto_energia_ordinale', { n: v })}</option>)}
                      </select>
                    </FormField>
                    <FormField label={t('richieste.campi.gse_potenza')}>
                      <input className={inputCls} type="number" min={0} step="0.01" value={String(formValues['gse_potenza_kw'] ?? '')} onChange={(e) => setField('gse_potenza_kw', e.target.value)} />
                    </FormField>
                    <FormField label={t('richieste.campi.gse_data_esercizio')}>
                      <input className={inputCls} type="date" value={String(formValues['gse_data_esercizio'] ?? '')} onChange={(e) => setField('gse_data_esercizio', e.target.value)} />
                    </FormField>
                    <FormField label={t('richieste.campi.gse_causale')}>
                      <select className={selectCls} value={String(formValues['gse_causale'] ?? '')} onChange={(e) => setField('gse_causale', e.target.value)}>
                        <option value="">{t('common.seleziona')}</option>
                        <option value="fine_vita">{t('richieste.campi.causale_fine_vita')}</option>
                        <option value="revamping">{t('richieste.campi.causale_revamping')}</option>
                        <option value="danno">{t('richieste.campi.causale_danno')}</option>
                        <option value="smontaggio">{t('richieste.campi.causale_smontaggio')}</option>
                        <option value="altro">{t('richieste.campi.causale_altro')}</option>
                      </select>
                    </FormField>
                    <FormField label={t('richieste.campi.gse_soggetto')}>
                      <select className={selectCls} value={String(formValues['gse_sogg_responsabile'] ?? '')} onChange={(e) => setField('gse_sogg_responsabile', e.target.value === 'true')}>
                        <option value="">{t('common.seleziona')}</option>
                        <option value="true">{t('common.si')}</option>
                        <option value="false">{t('common.no')}</option>
                      </select>
                    </FormField>
                    <FormField label={t('richieste.campi.gse_intestatario')}>
                      <input className={inputCls} value={String(formValues['gse_intestatario'] ?? '')} onChange={(e) => setField('gse_intestatario', e.target.value)} />
                    </FormField>
                    <FormField label={t('richieste.campi.gse_piva_cf')}>
                      <input className={inputCls} value={String(formValues['gse_piva_cf'] ?? '')} onChange={(e) => setField('gse_piva_cf', e.target.value)} />
                    </FormField>
                  </dl>
                ) : (
                  <dl className="divide-y divide-stone-mid">
                    <FieldRow label={t('richieste.campi.gse_codice_impianto')} value={r.gse_codice_impianto} mono />
                    <FieldRow label={t('richieste.campi.gse_censimp')} value={r.gse_censimp} mono />
                    <FieldRow label={t('richieste.campi.conto_energia')} value={r.gse_conto_energia ? t('richieste.campi.conto_energia_ordinale', { n: r.gse_conto_energia }) : null} />
                    <FieldRow label={t('richieste.campi.gse_potenza')} value={r.gse_potenza_kw != null ? r.gse_potenza_kw.toLocaleString('it-IT') : null} />
                    <FieldRow label={t('richieste.campi.gse_data_esercizio')} value={formatDateIT(r.gse_data_esercizio)} />
                    <FieldRow label={t('richieste.campi.gse_causale')} value={r.gse_causale} />
                    <FieldRow label={t('richieste.campi.gse_soggetto')} value={boolLabel(r.gse_sogg_responsabile)} />
                    <FieldRow label={t('richieste.campi.gse_intestatario')} value={r.gse_intestatario} />
                    <FieldRow label={t('richieste.campi.gse_piva_cf')} value={r.gse_piva_cf} mono />
                  </dl>
                )}
              </EditableCard>
            )}

            {/* ── Card Trasportatore ── */}
            {(hasTras || isEditingTras) && (
              <EditableCard
                title={t('richieste.dettaglio.card_trasportatore')}
                icon={Truck}
                {...editProps('trasportatore', trasportatoreInitialValues, trasportatorePayload)}
              >
                {isEditingTras ? (
                  <dl className="divide-y divide-stone-mid">
                    <FormField label={t('richieste.campi.tras_noto')}>
                      <select className={selectCls} value={String(formValues['tras_noto'] ?? 'false')} onChange={(e) => setField('tras_noto', e.target.value === 'true')}>
                        <option value="true">{t('common.si')}</option>
                        <option value="false">{t('common.no')}</option>
                      </select>
                    </FormField>
                    <FormField label={t('anagrafiche.campi.ragione_sociale')}>
                      <input className={inputCls} value={String(formValues['tras_ragione_sociale'] ?? '')} onChange={(e) => setField('tras_ragione_sociale', e.target.value)} />
                    </FormField>
                    <FormField label={t('richieste.campi.tras_anga')}>
                      <input className={inputCls} value={String(formValues['tras_anga'] ?? '')} onChange={(e) => setField('tras_anga', e.target.value)} />
                    </FormField>
                    <FormField label={t('richieste.campi.tras_note')}>
                      <textarea className={inputCls} rows={3} value={String(formValues['tras_note_logistiche'] ?? '')} onChange={(e) => setField('tras_note_logistiche', e.target.value)} />
                    </FormField>
                  </dl>
                ) : (
                  <dl className="divide-y divide-stone-mid">
                    <FieldRow label={t('richieste.campi.tras_noto')} value={boolLabel(r.tras_noto)} />
                    <FieldRow label={t('anagrafiche.campi.ragione_sociale')} value={r.tras_ragione_sociale} />
                    <FieldRow label={t('richieste.campi.tras_anga')} value={r.tras_anga} mono />
                    <FieldRow label={t('richieste.campi.tras_note')} value={r.tras_note_logistiche} />
                  </dl>
                )}
              </EditableCard>
            )}

            {/* ── Card Note ── */}
            {(r.note_cliente || isEditingNote) && (
              <EditableCard
                title={t('richieste.dettaglio.card_note_cliente')}
                icon={MessageSquare}
                {...editProps('note', noteInitialValues, notePayload)}
              >
                {isEditingNote ? (
                  <dl className="divide-y divide-stone-mid">
                    <FormField label={t('richieste.campi.note')}>
                      <textarea
                        className={inputCls}
                        rows={5}
                        value={String(formValues['note_cliente'] ?? '')}
                        onChange={(e) => setField('note_cliente', e.target.value)}
                      />
                    </FormField>
                  </dl>
                ) : (
                  <p className="text-sm text-ink-dark whitespace-pre-line">{r.note_cliente}</p>
                )}
              </EditableCard>
            )}

            {/* Card Note per utenti canEdit senza note preesistenti */}
            {canEdit && !r.note_cliente && !isEditingNote && (
              <EditableCard
                title={t('richieste.dettaglio.card_note_cliente')}
                icon={MessageSquare}
                {...editProps('note', noteInitialValues, notePayload)}
              >
                <p className="text-sm text-ink-soft italic">{t('richieste.dettaglio.nessuna_nota')}</p>
              </EditableCard>
            )}
          </div>

          {/* ── Colonna destra (40%) ── */}
          <div className="flex-[2] min-w-[280px] max-w-sm space-y-4">
            <WorkflowCard
              richiesta={r}
              userRole={userRole}
              onUpdated={(updated) => {
                setRichiesta(updated);
                refetchCount();
                setDocsRefreshKey((k) => k + 1);
              }}
              onToast={(msg) => {
                setWorkflowToast(msg);
              }}
            />
            {/* ── V75 — Nota INTERNA dell'ufficio ──────────────────────────
                Una sola nota, condivisa fra la pagina richiesta e quella
                ordine (stessa riga `orders`). Non esce in alcun documento. */}
            <EditableCard
              title={t('richieste.dettaglio.card_nota_interna')}
              icon={StickyNote}
              testid="card-nota-interna"
              canEdit={canEdit}
              isEditing={isEditingNotaInterna}
              onEditStart={() => handleEditStart('nota_interna', { nota_interna: r.nota_interna ?? '' })}
              onSave={handleSaveNotaInterna}
              onCancel={handleCancel}
              isSaving={isEditingNotaInterna && isSaving}
              error={isEditingNotaInterna ? saveError : null}
            >
              {isEditingNotaInterna ? (
                <textarea
                  className={inputCls}
                  rows={5}
                  data-testid="input-nota-interna"
                  placeholder={t('richieste.dettaglio.nota_interna_placeholder')}
                  value={String(formValues['nota_interna'] ?? '')}
                  onChange={(e) => setField('nota_interna', e.target.value)}
                />
              ) : r.nota_interna ? (
                <p className="text-sm text-ink-dark whitespace-pre-line" data-testid="testo-nota-interna">
                  {r.nota_interna}
                </p>
              ) : (
                <p className="text-sm text-ink-soft italic" data-testid="nota-interna-vuota">
                  {t('richieste.dettaglio.nessuna_nota_interna')}
                </p>
              )}
              {/* La firma resta anche a nota svuotata: dice chi l'ha cancellata. */}
              {r.nota_interna_updated_at && (
                <p className="mt-2 text-xs text-ink-soft" data-testid="firma-nota-interna">
                  {t('richieste.dettaglio.nota_interna_firma', {
                    nome: r.nota_interna_updated_by_nome ?? '—',
                    quando: new Date(r.nota_interna_updated_at).toLocaleString('it-IT'),
                  })}
                </p>
              )}
            </EditableCard>
            {r.omologa && id && (
              <OmologaOrdineCard richiestaId={id} omologa={r.omologa} />
            )}
            <ConsultazioniStorico consultazioni={r.consultazioni} />
            {id && (
              <ConferimentiCard
                ordine={{ id, status: r.status }}
                refreshSignal={docsRefreshKey}
              />
            )}
            {id && (r.status === 'concluso' || r.fatturato_ordine || r.pagato_ordine) && (
              <FatturazioneOrdineCard
                ordineId={id}
                status={r.status}
                fatturatoOrdine={r.fatturato_ordine}
                pagatoOrdine={r.pagato_ordine}
                conferimentiTotali={r.conferimenti_totali}
                conferimentiFatturati={r.conferimenti_fatturati}
                conferimentiPagati={r.conferimenti_pagati}
              />
            )}
            {DOCUMENTI_STATUSES.has(r.status) && id && (
              <DocumentiCard ordineId={id} refreshSignal={docsRefreshKey} omologa={r.omologa} />
            )}
            {/* Torna alla lista */}
            <button
              type="button"
              onClick={() => navigate(backPath)}
              className="w-full px-4 py-2 text-sm text-ink-soft border border-stone-border rounded-lg hover:bg-stone-bg transition-colors"
            >
              ← Torna a {backLabel.toLowerCase()}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
